import axios from 'axios';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

// Define the types for the pod metrics response
export interface PodMetric {
  cluster: string;
  namespace: string;
  pod_name: string;
  container_name: string;
  node_name: string;
  phase: string;
  display_name: string;
  restart_count: number;
  cpu_usage: {
    used: string;
    requested: string;
    limited: string;
    percent: number;
  };
  memory_usage: {
    used: string;
    requested: string;
    limited: string;
    percent: number;
  };
  created_at: string;
  restarted_at?: string;
  timestamp: string;
  event_reason?: string;
  record_type?: string;
  log_capture?: {
    log_lines: string[];
    capture_reason: string;
    capture_time: string;
    previous_container: boolean;
    log_level: string;
    max_lines: number;
  };
}

export interface PodMetricsResponse {
  metrics: PodMetric[];
  count: number;
  start_time: string;
  end_time: string;
  cluster: string;
}

// Define the state interface
interface PodMetricsState {
  metrics: PodMetric[];
  historicalMetrics: PodMetric[];
  count: number;
  cluster: string;
  loading: boolean;
  loadingHistory: boolean;
  error: string | null;
  historyError: string | null;
}

const initialState: PodMetricsState = {
  metrics: [],
  historicalMetrics: [],
  count: 0,
  cluster: '',
  loading: false,
  loadingHistory: false,
  error: null,
  historyError: null,
};

// Set the API base URL - use the proxy to avoid CORS issues
const KEYCLOAK_POD_METRICS_API_URL = 'https://api.status.gridbit.live/api/v1/pods/metrics';

// Create async thunk for fetching pod metrics
export const fetchKeycloakPodMetrics = createAsyncThunk(
  'keycloakPodMetrics/fetchKeycloakPodMetrics',
  async (cluster: string, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${KEYCLOAK_POD_METRICS_API_URL}?cluster=${cluster}&container=keycloak`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching pod metrics:', error);
      return rejectWithValue(error.response?.data || error.message || 'Failed to fetch pod metrics');
    }
  }
);

// Create the pod metrics slice
export const keycloakPodMetricsSlice = createSlice({
  name: 'keycloakPodMetrics',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchKeycloakPodMetrics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchKeycloakPodMetrics.fulfilled, (state, action: PayloadAction<PodMetricsResponse>) => {
        state.loading = false;
        state.metrics = action.payload.metrics;
        state.count = action.payload.count;
        state.cluster = action.payload.cluster;
      })
      .addCase(fetchKeycloakPodMetrics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default keycloakPodMetricsSlice.reducer; 